import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Box,
    Card,
    CardContent,
    TextField,
    Grid,
    Paper,
    Divider,
} from '@mui/material';
import { Save as SaveIcon, Edit as EditIcon } from '@mui/icons-material';
import ProductDescriptionBox from '../../ProductDescriptionBox';
import { useProductDescription } from '../../../contexts/ProductDescriptionContext';


interface ProductDescriptionConfig {
    title: string;
    content: string;
}

const AdminProductDescription: React.FC = () => {
    const { config, updateConfig, isLoading } = useProductDescription();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localConfig, setLocalConfig] = useState<ProductDescriptionConfig>({
        title: "Description produits",
        content: `Découvrez notre gamme complète de services de plongée sous-marine adaptés à tous les niveaux.
Que vous soyez débutant ou plongeur expérimenté, nous vous proposons des formations certifiantes,
des baptêmes inoubliables et des explorations guidées dans les plus beaux sites de la région.
Nos moniteurs diplômés vous accompagnent dans votre découverte du monde sous-marin avec sécurité et passion.`
    });

    useEffect(() => {
        if (config && config.title && config.content) {
            setLocalConfig(config);
        }
    }, [config]);

    const handleSave = async () => {
        setLoading(true);
        try {
            // Mettre à jour le contexte avec les nouvelles valeurs
            updateConfig(localConfig);

            // Simulation d'une sauvegarde
            await new Promise(resolve => setTimeout(resolve, 1000));

            setIsEditing(false);
            alert('Configuration sauvegardée avec succès !');
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setLocalConfig(config);
        setIsEditing(false);
    };

    const handleInputChange = (field: keyof ProductDescriptionConfig, value: string) => {
        setLocalConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
                <Typography variant="h6">Chargement de la configuration...</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Gestion du Pavé "Description Produits"
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                Modifiez le contenu du pavé qui apparaît sur toutes les fiches produits de votre site.
            </Typography>

            <Grid container spacing={3}>
                {/* Formulaire de modification */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <EditIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    {isEditing ? 'Modifier le contenu' : 'Contenu actuel'}
                                </Typography>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            <TextField
                                fullWidth
                                label="Titre du pavé"
                                value={localConfig.title}
                                onChange={(e) => handleInputChange('title', e.target.value)}
                                disabled={!isEditing}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Contenu du pavé"
                                multiline
                                rows={6}
                                value={localConfig.content}
                                onChange={(e) => handleInputChange('content', e.target.value)}
                                disabled={!isEditing}
                                sx={{ mb: 3 }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                {isEditing ? (
                                    <>
                                        <Button
                                            variant="contained"
                                            startIcon={<SaveIcon />}
                                            onClick={handleSave}
                                            disabled={loading}
                                        >
                                            {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleCancel}
                                            disabled={loading}
                                        >
                                            Annuler
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="contained"
                                        startIcon={<EditIcon />}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        Modifier
                                    </Button>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Aperçu du pavé */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Aperçu du pavé
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Voici comment le pavé apparaîtra sur votre site :
                                </Typography>

                                <Box sx={{ mt: 2 }}>
                                    <Box
                                        sx={{
                                            backgroundColor: '#e3f2fd',
                                            border: '2px solid #2196f3',
                                            borderRadius: 2,
                                            padding: 3,
                                            marginBottom: 3,
                                            boxShadow: '0 4px 8px rgba(33, 150, 243, 0.2)'
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            component="h2"
                                            gutterBottom
                                            sx={{ color: '#1976d2', fontWeight: 'bold' }}
                                        >
                                            {localConfig.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ color: '#424242', lineHeight: 1.6 }}
                                        >
                                            {localConfig.content}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default AdminProductDescription;
